import { delay, HttpResponse, http } from "msw";
import { apiPaths } from "../../api-paths.js";
import { emptyRoomInfo, roomsInfoResponseFake, roomsInfoResponseWithoutInfoFake } from "./get-rooms-info-200.js";
import type { Rooms_GetRoomsInfo_GetRoomsInfoQueryResponse_Success } from "../../swagger-types/v1/index.js";

const roomsInfoPath = `**${apiPaths.rooms.getRoomsInfo}`;

const readRequestedRoomIds = async (request: Request): Promise<number[]> => {
  const body = (await request.json()) as { roomIds?: number[] };
  return body.roomIds ?? [];
};

export const roomsInfoHandlersFactory = {
  create200: (response: Rooms_GetRoomsInfo_GetRoomsInfoQueryResponse_Success = roomsInfoResponseFake) => [
    http.post(roomsInfoPath, async () => HttpResponse.json(response, { status: 200 })),
  ],
  create200WithoutInfo: () => [
    http.post(roomsInfoPath, async () => HttpResponse.json(roomsInfoResponseWithoutInfoFake, { status: 200 })),
  ],
  create200EmptyContent: () => [
    http.post(roomsInfoPath, async ({ request }) => {
      const roomIds = await readRequestedRoomIds(request);
      return HttpResponse.json(
        {
          rooms: roomIds.map((roomId) => ({
            roomId,
            name: "Room",
            info: emptyRoomInfo,
          })),
        },
        { status: 200 },
      );
    }),
  ],
  createDelayed200: (
    delayMs = 1_500,
    response: Rooms_GetRoomsInfo_GetRoomsInfoQueryResponse_Success = roomsInfoResponseFake,
  ) => [
    http.post(roomsInfoPath, async () => {
      await delay(delayMs);
      return HttpResponse.json(response, { status: 200 });
    }),
  ],
  createInfiniteDelay: () => [
    http.post(roomsInfoPath, async () => {
      await delay("infinite");
      return HttpResponse.json(roomsInfoResponseFake, { status: 200 });
    }),
  ],
  createDeferred200: (response: Rooms_GetRoomsInfo_GetRoomsInfoQueryResponse_Success = roomsInfoResponseFake) => {
    let releaseRequest = () => {};
    const gate = new Promise<void>((resolve) => {
      releaseRequest = resolve;
    });

    return {
      handlers: [
        http.post(roomsInfoPath, async () => {
          await gate;
          return HttpResponse.json(response, { status: 200 });
        }),
      ],
      release: () => releaseRequest(),
    };
  },
  createDeferredEmptyContent: () => {
    let releaseRequest = () => {};
    const gate = new Promise<void>((resolve) => {
      releaseRequest = resolve;
    });

    return {
      handlers: [
        http.post(roomsInfoPath, async ({ request }) => {
          const roomIds = await readRequestedRoomIds(request);
          await gate;
          return HttpResponse.json(
            {
              rooms: roomIds.map((roomId) => ({
                roomId,
                name: "Room",
                info: emptyRoomInfo,
              })),
            },
            { status: 200 },
          );
        }),
      ],
      release: () => releaseRequest(),
    };
  },
  create422: () => [http.post(roomsInfoPath, async () => HttpResponse.json({}, { status: 422 }))],
  create502: () => [http.post(roomsInfoPath, async () => HttpResponse.json({}, { status: 502 }))],
  createDelayed502: (delayMs = 400) => [
    http.post(roomsInfoPath, async () => {
      await delay(delayMs);
      return HttpResponse.json({}, { status: 502 });
    }),
  ],
  createDeferred502: () => {
    let releaseRequest = () => {};
    const gate = new Promise<void>((resolve) => {
      releaseRequest = resolve;
    });

    return {
      handlers: [
        http.post(roomsInfoPath, async () => {
          await gate;
          return HttpResponse.json({}, { status: 502 });
        }),
      ],
      release: () => releaseRequest(),
    };
  },
  createDeferredNetworkError: () => {
    let releaseRequest = () => {};
    const gate = new Promise<void>((resolve) => {
      releaseRequest = resolve;
    });

    return {
      handlers: [
        http.post(roomsInfoPath, async () => {
          await gate;
          return HttpResponse.error();
        }),
      ],
      release: () => releaseRequest(),
    };
  },
  create500: () => [http.post(roomsInfoPath, async () => HttpResponse.json({}, { status: 500 }))],
  createError: () => [http.post(roomsInfoPath, async () => HttpResponse.error())],
  createDelayedNetworkError: (delayMs = 200) => [
    http.post(roomsInfoPath, async () => {
      await delay(delayMs);
      return HttpResponse.error();
    }),
  ],
  createNetworkErrorThen200: (
    response: Rooms_GetRoomsInfo_GetRoomsInfoQueryResponse_Success = roomsInfoResponseFake,
  ) => {
    let requestCount = 0;

    return [
      http.post(roomsInfoPath, async () => {
        requestCount += 1;

        return requestCount === 1 ? HttpResponse.error() : HttpResponse.json(response, { status: 200 });
      }),
    ];
  },
};
